import { useMutation } from '@apollo/client';
import { Box, Divider, Flex, Stack, useColorModeValue as mode } from '@chakra-ui/react';
import DashboardCard from '@components/atoms/DashboardCard';
import Button from '@components/interactive/Button';
import TextLink from '@components/interactive/TextLink';
import Body1 from '@components/typography/Body1';
import Header from '@components/typography/Header';
import SUBMIT_USER_BENEFIT_REQUEST from '@gql/SubmitUserBenefitRequest';
import medicineIcon from '@public/flaticons/medicine.svg';
import runningIcon from '@public/flaticons/running.svg';
import molarIcon from '@public/flaticons/molar.svg';
import glassesIcon from '@public/flaticons/glasses.svg';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { DashboardBenefit, UserBenefitRequestStatus } from '../types';
import { getCardContent } from './getBenefitContent';

const BENEFIT_ICON_PATHS = {
  OTC: medicineIcon,
  DENTAL: molarIcon,
  FITNESS: runningIcon,
  VISION: glassesIcon,
};

const REQUESTED_STRINGS = {
  SUBMITTED: 'Your request has been submitted. We will send you an email shortly.',
  PROCESSING: 'Your benefit request is being processed.',
  COMPLETED: 'Your benefit request is complete!',
  REJECTED: 'We have encountered an issue fufulling your benefit request.',
};

interface Props {
  userId: string;
  benefit: DashboardBenefit;
  // eslint-disable-next-line react/require-default-props
  onCompleted?: () => void;
}

const BenefitCard = ({ userId, benefit, onCompleted }: Props) => {
  const [status, setStatus] = useState<UserBenefitRequestStatus>(benefit.status);
  const router = useRouter();

  const { type, carrier_name, benefit_info } = benefit;

  const [submitUserBenefitRequest, { loading }] = useMutation(SUBMIT_USER_BENEFIT_REQUEST, {
    onCompleted: (data) => {
      setStatus(data?.submitUserBenefitRequest.status);
      onCompleted?.();
    },
    onError: () => {
      setStatus(UserBenefitRequestStatus.REJECTED);
    },
  });

  const { title, subtitle, buttonText, requestedButtonText } = getCardContent(carrier_name, type, benefit_info);

  const statusContent = REQUESTED_STRINGS[status] || REQUESTED_STRINGS[UserBenefitRequestStatus.PROCESSING];

  return (
    <DashboardCard>
      <Stack direction={['column', 'column', 'row']} alignItems={['stretch', 'stretch', 'normal']}>
        <Divider
          opacity={1}
          borderColor={mode('blue.500', 'blue.200')}
          borderWidth="2px"
          height="inherit"
          borderRadius={5}
          orientation="vertical"
          mr={[0, 0, 4]}
          ml={[0, 0, -5]}
          mb={[6, 6, 0]}
          flexDir="column"
        />
        <Box
          minW={['80px', '80px', '110px']}
          minH={['80px', '80px', '110px']}
          position="relative"
          display="flex"
          alignItems="center"
          mr={[0, 0, '16px !important']}
        >
          <Image src={BENEFIT_ICON_PATHS[type]} alt={`Icon for ${type.toLowerCase()} benefit`} loading="lazy" fill />
        </Box>
        <Box>
          <Stack pt={[6, 6, 0]}>
            <Header as="h4" display="inline-block">
              {title}
            </Header>
            {status === UserBenefitRequestStatus.REJECTED ? (
              <Box display="inline">
                <Body1 color="red.500" display="inline">
                  {statusContent} For more info, email{' '}
                </Body1>
                <TextLink href="mailto:accounts@fairsquaremedicare.com" text="accounts@fairsquaremedicare.com" />
              </Box>
            ) : (
              <Body1>{status ? statusContent : subtitle}</Body1>
            )}
            <Flex alignItems="center" pt={2}>
              <Button
                id="claim_benefit"
                className={`${type.toLowerCase()}-benefit`}
                width={['100%', '100%', '300px']}
                isDisabled={!!status}
                onClick={() => {
                  if (benefit.catalog_id) {
                    router.push({
                      pathname: '/account/catalog',
                      query: { id: benefit.id },
                    });
                  } else {
                    submitUserBenefitRequest({
                      variables: {
                        user_id: userId,
                        benefit_type: type,
                        benefit_id: benefit.id,
                        benefit_info,
                      },
                    });
                  }
                }}
              >
                {!loading && status ? requestedButtonText : buttonText}
              </Button>
            </Flex>
          </Stack>
        </Box>
      </Stack>
    </DashboardCard>
  );
};

export default BenefitCard;
